import { useQueries, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";

import {
  useSyncSourceNow,
  useUpdateSource,
} from "../features/admin/api/mutations";
import { adminQueries, viewerQueries } from "../features/admin/api/queries";
import { SourceCard } from "../features/admin/components/SourceCard";
import { gqlErrorMessage } from "../shared/lib/gql-error";
import { AppHeader } from "../shared/ui/AppHeader";

export const Route = createFileRoute("/_authenticated/admin/sources")({
  /*
   * Same shape as the review route: the viewer is loaded here so the component
   * can tell "not an admin" from "no sources", while the admin-only list is
   * fetched inside the component — a loader throw on FORBIDDEN would blank the
   * route instead of rendering the explanation.
   */
  loader: ({ context: { queryClient } }) =>
    queryClient.ensureQueryData(viewerQueries.current()),
  component: SourcesPage,
});

function SourcesPage() {
  const { data: viewerData } = useSuspenseQuery(viewerQueries.current());

  return (
    <>
      <AppHeader />
      <main className="mx-auto max-w-3xl p-6">
        <h1 className="text-2xl font-semibold">Sources</h1>

        {viewerData.viewer.isAdmin ? (
          <SourceList />
        ) : (
          <p className="mt-4 rounded-md bg-surface-muted px-3 py-2 text-sm text-content-muted">
            This page is for administrators. The server refuses these actions
            too — this screen is not what is stopping you.
          </p>
        )}
      </main>
    </>
  );
}

function SourceList() {
  const { data, error } = useSuspenseQuery({
    ...adminQueries.sources(),
    retry: false,
  });

  const sources = data.sources;

  /*
   * Run history per source, in parallel. `useQueries` rather than a query per
   * card so the whole page settles in one render pass — and each is keyed on its
   * own source id, so syncing one does not refetch the others' histories.
   */
  const runQueries = useQueries({
    queries: sources.map((source) => ({
      ...adminQueries.runs(source.id, 3),
      // The manual source has no runs and never will.
      enabled: source.kind !== "MANUAL",
    })),
  });

  const sync = useSyncSourceNow();
  const update = useUpdateSource();

  const failure = sync.error ?? update.error;

  if (error) {
    return (
      <p role="alert" className="mt-4 text-sm text-brand">
        {gqlErrorMessage(error, "Could not load the sources.")}
      </p>
    );
  }

  return (
    <>
      <p className="mt-1 text-sm text-content-muted">
        Where events come from. Each venue and organiser publishes differently,
        so each is worked out once and the answer recorded here.
      </p>

      {/*
        Stated plainly on the page, because it is the guarantee that makes
        everything below safe: syncing cannot put anything in front of users.
      */}
      <p className="mt-3 rounded-md border border-dashed border-border-subtle p-3 text-sm text-content-muted">
        Syncing never publishes. Everything a source brings in lands in the{" "}
        <Link to="/admin/review" className="text-brand underline">
          review queue
        </Link>{" "}
        for a person to decide on.
      </p>

      {failure && (
        <p role="alert" className="mt-4 text-sm text-brand">
          {gqlErrorMessage(
            failure,
            "That did not go through. Nothing was changed.",
          )}
        </p>
      )}

      {/* The last run's counts, surfaced immediately rather than only after a
          refetch — the number that landed in review is the answer to "did that
          button do anything". */}
      {sync.data && (
        <p className="mt-4 rounded-md bg-surface-muted px-3 py-2 text-sm">
          Staged {sync.data.syncSourceNow.staged}, updated{" "}
          {sync.data.syncSourceNow.updated}, skipped{" "}
          {sync.data.syncSourceNow.skipped}, dropped{" "}
          {sync.data.syncSourceNow.dropped}.{" "}
          {sync.data.syncSourceNow.staged > 0 && (
            <Link to="/admin/review" className="text-brand underline">
              Review them
            </Link>
          )}
        </p>
      )}

      <ul className="mt-4 flex flex-col gap-4">
        {sources.map((source, index) => (
          <SourceCard
            key={source.id}
            source={source}
            runs={runQueries[index]?.data?.syncRuns ?? []}
            busy={sync.isPending || update.isPending}
            onSync={(id) => {
              sync.mutate(id);
            }}
            onSave={(id, input) => {
              update.mutate({ id, input });
            }}
          />
        ))}
      </ul>
    </>
  );
}
